import { Button, Card, Checkbox, Form, Input, Modal, Radio, Space, Switch, Typography } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Grid as ZhGrid, GridItem as ZhGridItem } from "zhihao-ui-react";

import request from "@/api";
import { ADMIN_PUBLISH_PLAT_SAND } from "@zhihaoscm/meta";
import type { NotificationItem } from "./types";

export interface NotificationFormValues {
    title: string;
    content: string;
    classify: number;
    state: number;
    publishPlats: number[];
    activeFileIds: number[];
    unActiveFileIds: number[];
    publishTime?: string;
}

export interface NotificationFormPageProps {
    loadNotification?: (id: number | string) => Promise<NotificationItem>;
    saveNotification?: (id: number | string | undefined, values: NotificationFormValues) => Promise<void>;
}

/** 复用 Vue 通知详情接口，为编辑表单回填原始通知内容。 */
export async function loadNotification(id: number | string): Promise<NotificationItem> {
    const response = await request.service.get<NotificationItem>(`/dataService/admin/notification/${id}`);
    return response.data;
}

/** 复用 Vue 通知新增/修改接口，草稿与发布共用同一表单契约。 */
export async function saveNotification(id: number | string | undefined, values: NotificationFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/dataService/admin/notification", values);
    } else {
        await request.service.put(`/dataService/admin/notification/${id}`, values);
    }
}

const selectAllPublishPlat = ADMIN_PUBLISH_PLAT_SAND.SHIPPING_ALL.code;
const publishOptions = ADMIN_PUBLISH_PLAT_SAND.options.map(option => ({
    label: option.label,
    value: Number(option.value)
}));
const otherPublishPlats = publishOptions
    .filter(option => option.value !== selectAllPublishPlat)
    .map(option => option.value);

const EDITOR_FILE_HEADER = /^==ids=(.*?)\n/;

/** 从旧 Vue 富文本头部读取编辑前已关联的媒体文件。 */
export function getNotificationStoredFileIds(content?: string): number[] {
    const match = content?.match(EDITOR_FILE_HEADER);
    return (
        match?.[1]
            ?.split("--")
            .map(Number)
            .filter(value => Number.isFinite(value)) || []
    );
}

/** 从通知正文的图片、视频地址中提取仍在使用的文件 ID。 */
export function getNotificationActiveFileIds(content?: string): number[] {
    const activeFileIds: number[] = [];
    const mediaPattern = /<(?:img|video|source)\s+[^>]*src=["'][^"']*#(\d+)["']/gi;
    let match: RegExpExecArray | null;
    while ((match = mediaPattern.exec(content || "")) !== null) {
        const id = Number(match[1]);
        if (id && !activeFileIds.includes(id)) activeFileIds.push(id);
    }
    return activeFileIds;
}

/** 复用 Vue editorManage.setContent 的媒体文件头格式。 */
export function setNotificationEditorContent(content: string, activeFileIds: number[]): string {
    return activeFileIds.length ? `==ids=${activeFileIds.join("--")}\n${content}` : content;
}

/** 按 Vue 逻辑把“全部”标记转换为真实发布端数组，避免把 0 发送给接口。 */
function processPublishPlats(selectedPlats: number[] = []) {
    if (selectedPlats.includes(selectAllPublishPlat)) return otherPublishPlats;
    return selectedPlats.filter(plat => plat !== selectAllPublishPlat);
}

type CheckboxValue = string | number | boolean;

interface PublishPlatformCheckboxGroupProps {
    value?: CheckboxValue[];
    onChange?: (value: CheckboxValue[]) => void;
}

/** 复刻 Vue 全部发布端的选中、取消和具体端全选联动状态机。 */
function PublishPlatformCheckboxGroup({ value = [], onChange }: PublishPlatformCheckboxGroupProps) {
    const selectedPlats = value.map(Number);

    const handleChange = (nextValues: CheckboxValue[]) => {
        const nextPlats = nextValues.map(Number);
        const wasAllSelected = selectedPlats.includes(selectAllPublishPlat);
        const isAllSelected = nextPlats.includes(selectAllPublishPlat);

        // 点击“全部”时，选中全部具体端；再次点击时清空所有具体端。
        if (wasAllSelected !== isAllSelected) {
            onChange?.(isAllSelected ? [selectAllPublishPlat, ...otherPublishPlats] : []);
            return;
        }

        const allSpecificSelected = otherPublishPlats.every(plat => nextPlats.includes(plat));
        if (allSpecificSelected && !isAllSelected) {
            onChange?.([selectAllPublishPlat, ...otherPublishPlats]);
            return;
        }

        // 取消任一具体端时同步移除“全部”标记。
        onChange?.(isAllSelected ? nextPlats.filter(plat => plat !== selectAllPublishPlat) : nextPlats);
    };

    return <Checkbox.Group options={publishOptions} value={selectedPlats} onChange={handleChange} />;
}

/** 将 Vue 通知编辑返回的分类、状态和发布端枚举统一为表单使用的数字。 */
function normalizeNotificationFormValues(notification: NotificationItem["notification"]): NotificationFormValues {
    return {
        activeFileIds: [],
        classify: notification.classify == null ? 1 : Number(notification.classify),
        content: (notification.content || "").replace(EDITOR_FILE_HEADER, ""),
        publishPlats: (notification.publishPlatInfo || []).map(value => Number(value)),
        state: notification.state == null ? 1 : Number(notification.state),
        title: notification.title || "",
        unActiveFileIds: []
    };
}

/** 统一 Vue 通知草稿、即时发布和定时发布共用的媒体文件 payload。 */
function buildNotificationPayload(
    values: NotificationFormValues,
    state: number,
    lastFileIds: number[],
    publishTime?: string
): NotificationFormValues {
    const activeFileIds = getNotificationActiveFileIds(values.content);
    return {
        ...values,
        activeFileIds,
        content: setNotificationEditorContent(values.content, activeFileIds),
        state,
        publishPlats: processPublishPlats(values.publishPlats),
        unActiveFileIds: lastFileIds.filter(id => !activeFileIds.includes(id)),
        ...(publishTime ? { publishTime } : {})
    };
}

/** 平台通知新增/编辑页，保留旧 Vue 的草稿字段和发布端字段。 */
export function NotificationFormPage({
    loadNotification: load = loadNotification,
    saveNotification: save = saveNotification
}: NotificationFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const queryId = new URLSearchParams(location.search).get("id") || undefined;
    const [form] = Form.useForm<NotificationFormValues>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [publishValues, setPublishValues] = useState<NotificationFormValues | null>(null);
    const [publishVisible, setPublishVisible] = useState(false);
    const [timedPublish, setTimedPublish] = useState(false);
    const [publishDate, setPublishDate] = useState("");
    const [publishTime, setPublishTime] = useState("");
    const [lastFileIds, setLastFileIds] = useState<number[]>([]);

    useEffect(() => {
        if (!queryId) {
            setLastFileIds([]);
            form.setFieldsValue({
                activeFileIds: [],
                classify: 1,
                content: "",
                publishPlats: [],
                state: 1,
                title: "",
                unActiveFileIds: []
            });
            return;
        }
        void load(queryId)
            .then(result => {
                setLastFileIds(getNotificationStoredFileIds(result.notification.content));
                form.setFieldsValue(normalizeNotificationFormValues(result.notification));
            })
            .catch(() => setErrorMessage("通知详情加载失败"));
    }, [form, load, queryId]);

    /** 将表单定时发布时间转换为 Vue LOG_DATE_FORMAT_T 格式。 */
    const getPublishTime = () => {
        if (!publishDate || !publishTime) return "";
        return `${publishDate}T${publishTime.length === 5 ? `${publishTime}:00` : publishTime}`;
    };

    /** 切换定时发布时复刻 Vue 弹窗，默认填充当前时间后五分钟。 */
    const handleTimedPublishChange = (checked: boolean) => {
        setTimedPublish(checked);
        if (!checked) {
            setPublishDate("");
            setPublishTime("");
            return;
        }
        const target = new Date(Date.now() + 5 * 60 * 1000);
        const pad = (value: number) => String(value).padStart(2, "0");
        setPublishDate(`${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`);
        setPublishTime(`${pad(target.getHours())}:${pad(target.getMinutes())}`);
    };

    /** 确认发布通知，区分即时发布和定时发布状态。 */
    const confirmPublish = async () => {
        if (!publishValues) return;
        const scheduledTime = timedPublish ? getPublishTime() : "";
        if (timedPublish && !scheduledTime) {
            setErrorMessage("请选择发布时间");
            return;
        }
        try {
            await save(
                queryId,
                buildNotificationPayload(publishValues, timedPublish ? 2 : 3, lastFileIds, scheduledTime)
            );
            setPublishVisible(false);
            setPublishValues(null);
            void navigate("/userOperation/notificationManage");
        } catch {
            setErrorMessage("通知保存失败");
        }
    };

    /** 校验表单后先打开 Vue 发布确认弹窗，未确认前不发送请求。 */
    const submit = async (state: number) => {
        try {
            const values = await form.validateFields();
            if (state === 3) {
                setPublishValues(values);
                setTimedPublish(false);
                setPublishDate("");
                setPublishTime("");
                setPublishVisible(true);
                return;
            }
            await save(queryId, buildNotificationPayload(values, state, lastFileIds));
            void navigate("/userOperation/notificationManage");
        } catch {
            setErrorMessage("通知保存失败");
        }
    };

    return (
        <section aria-label="notification-form-page">
            <Card>
                <Typography.Title level={4}>{queryId ? "编辑通知" : "新增通知"}</Typography.Title>
                {errorMessage ? <p role="alert">{errorMessage}</p> : null}
                <Form form={form} layout="vertical">
                    <ZhGrid cols={4} gap={[40, 0]}>
                        <ZhGridItem span={4}>
                            <Form.Item label="分类" name="classify" rules={[{ required: true }]}>
                                <Radio.Group
                                    options={[
                                        { label: "系统通知", value: 1 },
                                        { label: "运营通知", value: 2 }
                                    ]}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="标题" name="title" rules={[{ required: true, message: "请输入标题" }]}>
                                <Input maxLength={50} />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item
                                label="发布端"
                                name="publishPlats"
                                rules={[{ required: true, message: "请选择发布端" }]}
                            >
                                <PublishPlatformCheckboxGroup />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="内容" name="content" rules={[{ required: true, message: "请输入内容" }]}>
                                <Input.TextArea rows={8} />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                    <Space>
                        <Button type="primary" onClick={() => void submit(1)}>
                            保存为草稿
                        </Button>
                        <Button aria-label="发布" onClick={() => void submit(3)}>
                            发布
                        </Button>
                        <Button onClick={() => void navigate("/userOperation/notificationManage")}>返回</Button>
                    </Space>
                </Form>
            </Card>
            <Modal
                open={publishVisible}
                title="是否发布"
                onCancel={() => setPublishVisible(false)}
                footer={
                    <Space>
                        <Button onClick={() => setPublishVisible(false)}>取消</Button>
                        <Button aria-label="确认发布" onClick={() => void confirmPublish()} type="primary">
                            发布
                        </Button>
                    </Space>
                }
            >
                <label className="flex items-center gap-2">
                    定时发布
                    <Switch aria-label="定时发布" checked={timedPublish} onChange={handleTimedPublishChange} />
                </label>
                {timedPublish ? (
                    <Space className="mt-4">
                        <Input
                            aria-label="发布时间日期"
                            type="date"
                            value={publishDate}
                            onChange={event => setPublishDate(event.target.value)}
                        />
                        <Input
                            aria-label="发布时间时间"
                            type="time"
                            value={publishTime}
                            onChange={event => setPublishTime(event.target.value)}
                        />
                    </Space>
                ) : null}
            </Modal>
        </section>
    );
}

export default NotificationFormPage;
